import React, { useEffect, useMemo, useState } from "react";
import api, {
  fetchMyOfferings,
  fetchOfferingMaterials,
  fetchOfferingWeeklyProgress,
} from "../api";
import "../App.css";

const pill = (bg, fg) => ({
  display: "inline-flex",
  padding: "4px 10px",
  borderRadius: 999,
  background: bg,
  color: fg,
  fontWeight: 800,
  fontSize: 12,
});

function offeringLabel(o) {
  const courseCode = o.course_code || o.course?.course_code || o.courseCode || "Course";
  const courseName = o.course_name || o.course?.course_name || o.courseName || "";
  const semester = o.semester || "";
  const batch = o.batch || "";
  const section = o.section || "";

  return `${courseCode}${courseName ? ` — ${courseName}` : ""}${semester ? ` | ${semester}` : ""}${batch ? ` | ${batch}` : ""}${section ? ` ${section}` : ""}`;
}

function offeringCourseId(o) {
  return o.course_id || o.course?.id || o.courseId || "";
}

function offeringCourseCode(o) {
  return o.course_code || o.course?.course_code || o.courseCode || "";
}

export default function Dashboard({ user }) {
  const [offerings, setOfferings] = useState([]);
  const [offeringId, setOfferingId] = useState("");

  const [materials, setMaterials] = useState([]);
  const [progress, setProgress] = useState(null);
  const [quality, setQuality] = useState(null);
  const [reminderOverview, setReminderOverview] = useState(null);

  const [batches, setBatches] = useState([]);
  const [feedback, setFeedback] = useState(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const selectedOffering = useMemo(
    () => offerings.find((o) => String(o.id) === String(offeringId)),
    [offerings, offeringId]
  );

  const courseId = selectedOffering ? offeringCourseId(selectedOffering) : "";

  const loadBase = async () => {
    setErr("");
    setLoading(true);

    try {
      const oRes = await fetchMyOfferings();
      const list = oRes.data || [];

      setOfferings(list);

      if (!offeringId && list.length) {
        setOfferingId(list[0].id);
      }

      try {
        const bRes = await api.get("/feedback/batches");
        setBatches(bRes.data || []);
      } catch {
        setBatches([]);
      }

      try {
        const rRes = await api.get("/reminders/overview");
        setReminderOverview(rRes.data || null);
      } catch {
        setReminderOverview(null);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const loadOfferingData = async (id) => {
    if (!id) return;

    setErr("");

    const currentOffering = offerings.find((o) => String(o.id) === String(id));
    const currentCourseId = currentOffering ? offeringCourseId(currentOffering) : "";

    const tasks = [
      fetchOfferingMaterials(id)
        .then((r) => setMaterials(r.data || []))
        .catch(() => setMaterials([])),

      fetchOfferingWeeklyProgress(id)
        .then((r) => setProgress(r.data || null))
        .catch(() => setProgress(null)),
    ];

    if (currentCourseId) {
      tasks.push(
        api
          .get(`/courses/${currentCourseId}/quality-score`)
          .then((r) => setQuality(r.data || null))
          .catch(() => setQuality(null))
      );
    } else {
      setQuality(null);
    }

    await Promise.all(tasks);

    try {
      const latest = batches && batches.length ? batches[0] : "";
      const code = currentOffering ? offeringCourseCode(currentOffering) : "";

      if (!latest || !code) {
        setFeedback(null);
        return;
      }

      const fRes = await api.get("/feedback", {
        params: {
          batch: latest,
          course: code,
        },
      });

      setFeedback(fRes.data || null);
    } catch {
      setFeedback(null);
    }
  };

  useEffect(() => {
    loadBase();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!offeringId) return;
    loadOfferingData(offeringId);
    // eslint-disable-next-line
  }, [offeringId, offerings.length]);

  useEffect(() => {
    if (!offeringId) return;
    loadOfferingData(offeringId);
    // eslint-disable-next-line
  }, [batches]);

  const welcomeName = user?.full_name || user?.username || "User";

  const recentMaterials = (materials || []).slice(0, 6);
  const weeksBehind = progress?.weeks_behind || [];
  const overall = quality?.overall_score ?? null;

  if (loading) {
    return (
      <div className="fade-in">
        <div className="page-header">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Loading your QA overview…</p>
        </div>

        <div className="card" style={{ padding: 18 }}>
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div
        className="page-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 className="page-title">Welcome back, {welcomeName}!</h1>
          <p className="page-subtitle">
            Live QA overview from offering-based uploads, weekly progress,
            quality score, feedback, reminders and escalations.
          </p>
        </div>

        <div style={{ minWidth: 340 }}>
          <div
            style={{
              fontSize: 12,
              color: "#64748b",
              marginBottom: 6,
              fontWeight: 800,
            }}
          >
            Course Offering / Class Section
          </div>

          <select
            className="form-input"
            value={offeringId}
            onChange={(e) => setOfferingId(e.target.value)}
          >
            {offerings.length === 0 ? (
              <option value="">No assigned course offerings</option>
            ) : (
              offerings.map((o) => (
                <option key={o.id} value={o.id}>
                  {offeringLabel(o)}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      {err && (
        <div
          className="card"
          style={{
            border: "1px solid #fecaca",
            background: "#fff1f2",
            padding: 14,
            marginBottom: 16,
          }}
        >
          <div style={{ color: "#991b1b", fontWeight: 800 }}>{err}</div>
        </div>
      )}

      <div className="dashboard-grid">
        <div className="stats-card">
          <div className="stats-number">{offerings.length}</div>
          <div className="stats-label">Assigned Offerings</div>
        </div>

        <div className="stats-card">
          <div className="stats-number">{materials?.length || 0}</div>
          <div className="stats-label">Offering Materials</div>
        </div>

        <div className="stats-card">
          <div className="stats-number">{weeksBehind.length}</div>
          <div className="stats-label">Weeks Behind</div>
        </div>

        <div className="stats-card">
          <div className="stats-number">
            {overall === null ? "—" : `${overall}%`}
          </div>
          <div className="stats-label">Quality Score</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2 className="card-title">Reminder & Escalation Engine</h2>
        </div>

        <div className="card-content">
          {!reminderOverview ? (
            <div style={{ color: "#64748b" }}>No reminder data available.</div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  marginBottom: 18,
                }}
              >
                <div
                  style={{
                    background: "#fef9c3",
                    border: "1px solid #fde68a",
                    borderRadius: 14,
                    padding: 16,
                  }}
                >
                  <div style={{ fontSize: 13, color: "#854d0e", fontWeight: 800 }}>
                    Active Reminders
                  </div>

                  <div style={{ fontSize: 30, fontWeight: 900, color: "#713f12" }}>
                    {reminderOverview.total_reminders || 0}
                  </div>
                </div>

                <div
                  style={{
                    background: "#fee2e2",
                    border: "1px solid #fecaca",
                    borderRadius: 14,
                    padding: 16,
                  }}
                >
                  <div style={{ fontSize: 13, color: "#991b1b", fontWeight: 800 }}>
                    Escalations
                  </div>

                  <div style={{ fontSize: 30, fontWeight: 900, color: "#7f1d1d" }}>
                    {reminderOverview.total_escalations || 0}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
        }}
      >
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Recent Offering Materials</h2>
          </div>

          <div className="card-content">
            {!offeringId ? (
              <div style={{ color: "#64748b" }}>Select a course offering.</div>
            ) : recentMaterials.length === 0 ? (
              <div style={{ color: "#64748b" }}>
                No materials yet for this offering.
              </div>
            ) : (
              recentMaterials.map((m, idx) => (
                <div
                  key={m.id || idx}
                  style={{
                    padding: "12px 0",
                    borderBottom:
                      idx < recentMaterials.length - 1
                        ? "1px solid #e2e8f0"
                        : "none",
                  }}
                >
                  <div style={{ fontWeight: 800, fontSize: 14 }}>
                    {m.title}
                  </div>

                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                    {` · Files: ${(m.files || []).length}`}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Course Health</h2>
          </div>

          <div className="card-content">
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 900 }}>Weekly Progress</div>

              {!progress ? (
                <div style={{ color: "#64748b", marginTop: 6 }}>
                  No weekly progress yet.
                </div>
              ) : (
                <>
                  <div style={{ marginTop: 8, color: "#64748b", fontSize: 12 }}>
                    Offering: <b>{selectedOffering ? offeringLabel(selectedOffering) : ""}</b> · Weeks behind:{" "}
                    <b>{weeksBehind.length}</b>
                  </div>

                  {weeksBehind.length ? (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#991b1b" }}>
                        Behind weeks
                      </div>

                      <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {weeksBehind.slice(0, 16).map((w) => (
                          <span key={w} style={pill("#fee2e2", "#991b1b")}>
                            W{w}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 8, color: "#166534", fontWeight: 800 }}>
                      On track 🎉
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 14 }}>
              <div style={{ fontWeight: 900 }}>Latest Feedback Snapshot</div>

              {!feedback ? (
                <div style={{ color: "#64748b", marginTop: 6 }}>
                  No feedback loaded, or feedback endpoint is not available for this course.
                </div>
              ) : (
                <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <span style={pill("#dcfce7", "#166534")}>
                      Pos: {feedback?.sentiment?.pos ?? 0}
                    </span>
                    <span style={pill("#fef9c3", "#854d0e")}>
                      Neu: {feedback?.sentiment?.neu ?? 0}
                    </span>
                    <span style={pill("#fee2e2", "#991b1b")}>
                      Neg: {feedback?.sentiment?.neg ?? 0}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                borderTop: "1px solid #e2e8f0",
                paddingTop: 14,
                marginTop: 14,
              }}
            >
              <div style={{ fontWeight: 900 }}>Quality Summary</div>

              {!quality ? (
                <div style={{ color: "#64748b", marginTop: 6 }}>
                  No quality score found. Recompute score from the Reports page.
                </div>
              ) : (
                <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <span style={pill("#e0f2fe", "#075985")}>
                      Overall: {quality.overall_score ?? 0}%
                    </span>
                    <span style={pill("#dcfce7", "#166534")}>
                      Complete: {quality.completeness_score ?? 0}%
                    </span>
                    <span style={pill("#fef9c3", "#854d0e")}>
                      Align: {quality.alignment_score ?? 0}%
                    </span>
                    <span style={pill("#f3e8ff", "#6b21a8")}>
                      Feedback: {quality.feedback_score ?? 0}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}